import { H1, H2 } from "@/components/Heading";
import { Button, HStack, Stack, Textarea } from "@chakra-ui/react";
import React, { useState } from "react";
import { FormInput } from "@/components/Form/FormInput";
import type { CreateFeedbackDto } from "@/api/types";
import type { FeedbackSentiment } from "@/api/types";
import { useSearch } from "@tanstack/react-router";
import { useApiMutate } from "@/api/hooks/useApiMutate";
import { api } from "@/api";
import toast from "react-hot-toast";
import { FeedbackSentimentSelect } from "./FeedbackSentimentSelect";
import { telemetry } from "@/telemetry";
import type { RequestResult } from "@/telemetry/events";
import { feedbackSentiments } from "@/telemetry/events";

const trackFeedbackSubmitted = (
  feedback: CreateFeedbackDto,
  result: RequestResult,
) =>
  telemetry.trackEvent("feedback_submitted", {
    sentiment: feedbackSentiments[feedback.sentiment],
    has_comment: !!feedback.comment.trim(),
    source: "feedback_page",
    result,
  });

export const FeedbackPage = () => {
  const { feedbackSentiment, orderId } = useSearch({ strict: false });
  const [state, setState] = useState<CreateFeedbackDto>({
    comment: "",
    email: "",
    orderId: Number(orderId),
    sentiment: feedbackSentiment as FeedbackSentiment,
  });

  const [wasSubmitted, setWasSubmitted] = useState(false);

  const isDisabled = !state.comment || !state.sentiment;

  const apiSubmit = useApiMutate({
    mutationFn: api.createFeedback,
    onSuccess: (_, feedback) => {
      trackFeedbackSubmitted(feedback, "ok");
      setWasSubmitted(true);
    },
    onError: (_, feedback) => {
      trackFeedbackSubmitted(feedback, "error");
      toast.error(
        "There was an error submitting your feedback. Please try again.",
      );
    },
  });

  const handleSentimentSelect = (sentiment: FeedbackSentiment) => {
    telemetry.trackEvent("feedback_sentiment_selected", {
      sentiment: feedbackSentiments[sentiment],
      source: "feedback_page",
    });
    setState((prev) => ({ ...prev, sentiment }));
  };

  const handleSubmit = () =>
    apiSubmit.mutate({
      comment: state.comment,
      email: state.email,
      orderId: state.orderId,
      sentiment: state.sentiment!,
    });

  return (
    <Stack spacing="14">
      <Stack
        spacing="8"
        alignItems="center"
        position="relative"
      >
        <H1>We appreciate your feedback</H1>
        <H2>Please take a moment to let us know how we did.</H2>

        <FeedbackSentimentSelect
          value={state.sentiment}
          onSelect={handleSentimentSelect}
          isDisabled={wasSubmitted}
        />

        <Stack
          maxWidth="640px"
          width="full"
          spacing="8"
        >
          <Textarea
            rows={4}
            width="full"
            placeholder="Type feedback here (required)"
            value={state.comment}
            onChange={(e) =>
              setState((prev) => ({ ...prev, comment: e.target.value }))
            }
          />

          <HStack spacing="14">
            <FormInput
              flex="1"
              label="Email address (optional)"
              value={state.email}
              onChange={(e) =>
                setState((prev) => ({ ...prev, email: e.target.value }))
              }
            />
            <Button
              mt="auto"
              isDisabled={isDisabled || wasSubmitted}
              isLoading={apiSubmit.isPending}
              onClick={handleSubmit}
            >
              Submit
            </Button>
          </HStack>
        </Stack>
      </Stack>

      {wasSubmitted && <H2 textAlign="center">Thank you for your feedback.</H2>}
    </Stack>
  );
};
